import { Button } from "@/components/ui/button";
import { getJwtFromStorage, parseVolumeJwt, validateSession } from "@/lib/auth";
import { StorageUtility } from "@/lib/storage";
import { decideFxAiRequest, getFxAiPendingRequest } from "@/modules/fx-ai/api";
import { AuthCard } from "@/routes/auth/_auth-layout/-components/auth/auth-card";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { z } from "zod/v4";

export const Route = createFileRoute("/auth/_auth-layout/fx-ai/approve")({
  validateSearch: zodValidator(z.object({ requestId: z.string().min(1) })),
  beforeLoad: () => {
    const token = getJwtFromStorage();
    validateSession(token);
    return { token };
  },
  component: FxAiApprovePage,
});

function FxAiApprovePage() {
  const { requestId } = Route.useSearch();
  const { token } = Route.useRouteContext();
  const session = parseVolumeJwt(token);
  // The merchant FX MCP server refuses admin tokens, impersonating or not.
  const isAdminSession = Boolean(session.adminId);
  const [chosenApplicationId, setApplicationId] = useState<string>();
  const pending = useQuery({
    queryKey: ["fx-ai-pending", requestId, session.userAccountId, session.jti],
    queryFn: () => getFxAiPendingRequest(requestId, token),
    enabled: !isAdminSession,
    retry: false,
  });
  // Pre-select the only application, or the one last open on the MCP setup page.
  const applications = pending.data?.applications ?? [];
  const remembered = StorageUtility.getItem("v_fxMcpApplicationId_v1");
  const applicationId =
    chosenApplicationId ??
    (applications.length === 1
      ? applications[0].id
      : (applications.find((application) => application.id === remembered)
          ?.id ?? ""));
  const decision = useMutation({
    mutationFn: (choice: "approve" | "deny") =>
      decideFxAiRequest(
        {
          requestId,
          decision: choice,
          applicationId: choice === "approve" ? applicationId : undefined,
        },
        token,
      ),
    onSuccess: ({ redirectUrl }) => {
      window.location.assign(redirectUrl);
    },
  });

  return (
    <main>
      <AuthCard
        title="Allow access to your FX data?"
        description="Review this connection before continuing to your assistant."
        badgeText="FX access request"
        footer="Only approve a connection you started in your AI assistant."
      >
        {isAdminSession && (
          <p className="text-sm text-V_Base90" role="alert">
            Admin accounts can't connect to the merchant FX MCP server. Sign in
            as a merchant user, or use FX MCP Server in Orbit.
          </p>
        )}
        {!isAdminSession && pending.isPending && (
          <p className="text-sm text-V_Base90">Loading connection request…</p>
        )}
        {pending.isError && (
          <p className="text-sm text-V_Base90" role="alert">
            This connection request has expired or is no longer available. Start
            again from your AI assistant.
          </p>
        )}
        {pending.data && (
          <div className="flex flex-col gap-5">
            <p className="text-sm leading-6 text-V_Base90">
              <strong className="text-V_Black">
                {pending.data.clientName}
              </strong>{" "}
              wants to read your FX rates, trades and payouts.
            </p>
            <div className="flex flex-col gap-3 rounded-xl border border-V_Main20 bg-pageBackground p-4 text-sm">
              <div>
                <p className="text-xs font-medium text-V_Base90">Permission</p>
                <p className="font-semibold text-V_Black">
                  Read FX rates, trades and payouts
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-V_Base90">Return to</p>
                <p className="break-all font-mono text-xs text-V_Black">
                  {new URL(pending.data.redirectUri).origin}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <label htmlFor="fx-ai-application" className="font-semibold">
                FX application
              </label>
              <select
                id="fx-ai-application"
                className="w-full rounded-lg border border-V_Main20 bg-white px-3 py-2.5"
                value={applicationId}
                onChange={(event) => setApplicationId(event.target.value)}
              >
                <option value="">Select an application</option>
                {pending.data.applications.map((application) => (
                  <option key={application.id} value={application.id}>
                    {application.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-V_Base90">
                The assistant can only read data for the application you choose.
              </p>
            </div>
            {decision.isError && (
              <p className="text-sm text-red-600" role="alert">
                Could not complete the connection. Please try again.
              </p>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                className="flex-1"
                onClick={() => decision.mutate("approve")}
                disabled={decision.isPending || !applicationId}
              >
                Allow access
              </Button>
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => decision.mutate("deny")}
                disabled={decision.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </AuthCard>
    </main>
  );
}
